import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md'
}) => {
    const sizeClasses = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-2xl',
        xl: 'sm:max-w-4xl',
        '2xl': 'sm:max-w-5xl',
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" />
                <Dialog.Content
                    className={cn(
                        "fixed inset-x-3 top-4 bottom-4 z-[60] w-auto sm:left-1/2 sm:right-auto sm:top-1/2 sm:bottom-auto sm:w-[95vw] sm:-translate-x-1/2 sm:-translate-y-1/2",
                        "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-[#2A2A2A] bg-surface shadow-2xl animate-slide-in-up",
                        sizeClasses[size]
                    )}
                >
                    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#2A2A2A] px-5 py-4 sm:px-6">
                        <div>
                            <Dialog.Title className="text-lg font-bold text-white">{title}</Dialog.Title>
                            {description && (
                                <Dialog.Description className="text-sm text-muted-foreground mt-0.5">
                                    {description}
                                </Dialog.Description>
                            )}
                        </div>
                        <Dialog.Close asChild>
                            <button className="p-2 rounded-xl hover:bg-[#1A1A1A] text-muted-foreground hover:text-white transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </Dialog.Close>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
                        {children}
                    </div>

                    {footer && (
                        <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#2A2A2A] bg-[#1A1A1A]/50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                            {footer}
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Modal;
